import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { DisablePrepaymentConfirmModalConfig } from '@front-platform/content-builder';
import { disablePrepaymentConfirmModalResolveConfig } from '@front-platform/content-builder';
import { CommonModule } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalsService } from '@core/services/modals/modals.service';
import { ModalFillerAtomComponent } from '@shared/atoms/modal-filler-atom/modal-filler-atom.component';
import { map } from 'rxjs';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';

export type DisablePrepaymentModalData = ModalConfig<{
  handle: typeof DisablePrepaymentConfirmModalComponent.handle;
  outputs: { type: 'userChoice'; data: boolean };
}>;
export interface DisablePrepaymentConfirmModalRuntimeConfig {}

export type DisablePrepaymentConfirmModalConfigInput = Partial<
  Omit<DisablePrepaymentConfirmModalConfig, keyof DisablePrepaymentConfirmModalRuntimeConfig> &
    DisablePrepaymentConfirmModalRuntimeConfig
>;
export type DisablePrepaymentConfirmModalResolvedConfig = Omit<
  DisablePrepaymentConfirmModalConfig,
  keyof DisablePrepaymentConfirmModalRuntimeConfig
> &
  Partial<DisablePrepaymentConfirmModalRuntimeConfig>;
export interface DisablePrepaymentConfirmModalContract extends ConfigComponentContract<
  DisablePrepaymentConfirmModalConfigInput,
  DisablePrepaymentConfirmModalResolvedConfig
> {}

@Component({
  selector: 'app-disable-prepayment-modal',
  standalone: true,
  imports: [CommonModule, ModalFillerAtomComponent, ButtonAtomComponent, TranslateModule],
  template: `
    <app-modal-filler-atom
      (onClick)="close()"
      [index]="index()"
    ></app-modal-filler-atom>
    <div
      class="fixed left-0 right-0 overflow-y-auto shadow md:left-0 md:top-0 md:flex md:h-full md:w-full md:items-center md:justify-center mdMax:bottom-0 mdMax:mx-auto mdMax:max-h-85-screen mdMax:max-w-screen-sm"
      [style.z-index]="index() + 50"
    >
      @if (isDesktop()) {
        <app-modal-filler-atom
          [withBackdrop]="false"
          (onClick)="close()"
          [index]="index()"
        ></app-modal-filler-atom>
      }
      <div
        class="flex flex-col rounded-t-3xl bg-white md:max-w-lg md:rounded-3xl"
        [style.z-index]="index() + 50"
      >
        <div class="space-y-6 p-6">
          <div class="flex items-center">
            <div class="flex flex-1 items-center">
              <i class="icon icon-handout mr-3 text-xl"></i>
              <span class="font-accent font-bold text-xl">{{
                'disable-prepayment-modal.title' | translate
              }}</span>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <p>{{ 'disable-prepayment-modal.confirmation-message' | translate }}</p>
          </div>

          <div class="flex items-center justify-center gap-5">
            <app-button-atom
              class="block flex-1"
              [label]="'disable-prepayment-modal.no' | translate"
              [type]="'outlined'"
              [color]="'primary'"
              [full]="true"
              (clickEventEmitter)="onUserChoice(false)"
            />
            <app-button-atom
              class="block flex-1"
              [label]="'disable-prepayment-modal.yes' | translate"
              [type]="'primary'"
              [color]="'primary'"
              [full]="true"
              (clickEventEmitter)="onUserChoice(true)"
            />
          </div>
        </div>
      </div>
    </div>
  `,
})
export class DisablePrepaymentConfirmModalComponent
  extends ModalScrollBlockBase
  implements DisablePrepaymentConfirmModalContract
{
  readonly config = input<DisablePrepaymentConfirmModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    DisablePrepaymentConfirmModalConfig,
    DisablePrepaymentConfirmModalRuntimeConfig
  >(disablePrepaymentConfirmModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'disable-prepayment-confirm-modal' as const;

  private readonly modalsService: ModalsService<DisablePrepaymentModalData> = inject(ModalsService);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);

  constructor() {
    super();
  }

  protected readonly index = toSignal(
    this.modalsService
      .getData('disable-prepayment-confirm-modal')
      .pipe(map(el => (el?.index ? el.index : 4))),
    { initialValue: 4 },
  );

  protected readonly isDesktop = toSignal(this.settingsStoreSelector.selectDeviceIsDesktopScreen);

  onUserChoice(choice: boolean): void {
    this.modalsService.emitEvent({
      handle: 'disable-prepayment-confirm-modal',
      data: {
        type: 'userChoice',
        data: choice,
      },
    });
    this.modalsService.close('disable-prepayment-confirm-modal');
  }

  protected close(): void {
    this.onUserChoice(false);
    this.modalsService.close('disable-prepayment-confirm-modal');
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(DisablePrepaymentConfirmModalComponent, {
  'keyAliases': {},
});
